/* eslint-disable no-nested-ternary */
/* eslint-disable prettier/prettier */
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { moneyFormat } from '@helpers';
import { useTranslation } from 'react-i18next';
import { useBreakpoints } from '@hooks/NewBreakpoint';
import moment from 'moment';
import { useNearScreen, useTransaction } from 'hooks';
import { useSelector, useDispatch } from 'react-redux';
import { setTransactionReference } from '@store/transactions/actions';
import TableSkeleton, { Table, RowBody, HeaderRow } from './TableSkeleton';
import LoadingTransactions from '../../Titles/LoadingTransactions';
import ErrorTable from './ErrorTable';

export const Text = styled.div(
  ({ theme }) => css`
    ${theme.textTypes['Subtitle-1-w500']}
    color: #707070;
    font-weight: 600;
    font-size: 14px;
    width: 100%;
    text-align: center;
    margin-bottom: 16px;
    margin-top: 16px;
    padding-bottom: 16px;
  `
);

const HeaderCol = styled.div`
  color: #adadad;
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px;
  width: ${(props) => props.width};
  text-align: ${(props) => props.right && 'right'};
  margin-left: ${(props) => props.marginLeft};
`;

const RowTotal = styled.div`
  padding: 16px 8px 16px 8px;
  border-radius: 8px;
  background: #ebebeb;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 50px;
`;

const TotalLayout = styled.div`
  display: flex;
  width: 100%;
  padding: 8px 24px;
  margin-top: 8px;
`;

export const CellBody = styled.div`
  color: #505050;
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  width: 100%;
  font-weight: ${(props) => props.isBold && '700'};
  text-align: ${(props) => props.right && 'right'};
  width: ${(props) => props.width};
  padding-left: ${(props) => props.paddingLeft};
  margin-left: ${(props) => props.marginLeft};
  @media (min-width: 936px) {
    font-size: 14px;
  }
`;

const Title = styled.div`
  color: #505050;
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
  margin-top: 24px;
  padding-left: 24px;
`;

const LayoutTotal = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
`;

const TotalAmountDetail = styled.div`
  color: #444b62;
  text-align: right;
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
  width: ${(props) => props.width};
  paddingleft: ${(props) => props.paddingLeft};
  text-align: ${(props) => props.textAlign};
`;

const TableTransactionsModal = ({ data, handleCloseError }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const { getTransactionsDetails } = useTransaction();

  const dispatch = useDispatch();

  const { breakpoint } = useBreakpoints();
  const externalRef = useRef();
  const { isNearScreen } = useNearScreen({ externalRef, once: false });

  useEffect(() => {
    if (isNearScreen) {
      getTransactionsDetails(data.details_transaction.reference_number, currentPage + 1);
      setCurrentPage(currentPage + 1);
    }
  }, [isNearScreen]);

  const { errorTransactionsDetails, loadingTransactionsDetails } = useSelector(
    (state) => state.transactions
  );

  const { settlements: orderSummary, pagination } = useSelector(
    (state) => state.transactions.transactionDetails?.data || []
  );

  useEffect(() => {
    dispatch(setTransactionReference(data.details_transaction.reference_number));
    getTransactionsDetails(data.details_transaction.reference_number);
  }, []);

  return (
    <>
      {errorTransactionsDetails ? (
        <>
          <ErrorTable handleCloseError={handleCloseError} />
        </>
      ) : !orderSummary ? (
        <TableSkeleton />
      ) : (
        <>
          <Title>{t('transactions.modalDetail.details')}</Title>
          <Table tableHeight={orderSummary.length === 2}>
            <HeaderRow>
              <HeaderCol width="145px">{t('transactions.modalDetail.numAccountState')}</HeaderCol>

              <HeaderCol
                width="114px"
                marginLeft={['xs', 'sm', 'ss', 'smm'].includes(breakpoint) && '8px'}
              >
                {t('transactions.modalDetail.dateOrder')}
              </HeaderCol>
              <HeaderCol right width="121px">
                {t('transactions.modalDetail.amountToPay')}
              </HeaderCol>
            </HeaderRow>
            {orderSummary
              .filter((item) => item.payout?.centAmount > 0)
              .map((order) => (
                <RowBody>
                  <>
                    <CellBody width="145px">{order.number}</CellBody>
                    <CellBody
                      width="114px"
                      marginLeft={['xs', 'sm', 'ss', 'smm'].includes(breakpoint) && '8px'}
                    >
                      {moment(order.openedAt).format('DD/MM/YYYY HH:mm')}
                    </CellBody>
                    {order.payout && (
                      <CellBody right width="121px" isBold>
                        {moneyFormat(
                          order.payout?.centAmount / (order.payout?.fraction || 1),
                          true
                        )}
                      </CellBody>
                    )}
                  </>
                </RowBody>
              ))}
            {orderSummary.length !== pagination.total && <div id="visor" ref={externalRef} />}
            {loadingTransactionsDetails && <LoadingTransactions />}
            {orderSummary.length > 4 && orderSummary.length === pagination.total && (
              <Text>{t('transactionsFalabella.noMoreTransactions')}</Text>
            )}
          </Table>
          <TotalLayout>
            <RowTotal>
              <LayoutTotal>
                <TotalAmountDetail width="74px" textAlign="inherit">
                  {t('transactions.modalDetail.total')}
                </TotalAmountDetail>
                <TotalAmountDetail width="121px">
                  {moneyFormat(data.gross_value, true)}
                </TotalAmountDetail>
              </LayoutTotal>
            </RowTotal>
          </TotalLayout>
        </>
      )}
    </>
  );
};

export default TableTransactionsModal;
